// Header.tsx

import React, { useState, useEffect, useRef } from "react";
import MarTechLogo from "../../assets/LogoMartechSol.png";
import { IoNotificationsSharp } from "react-icons/io5";
import { MdOutlineMoreTime, MdOutlineTimerOff } from "react-icons/md";
import { RiLogoutCircleRLine } from "react-icons/ri";
import { FaClipboardList } from "react-icons/fa";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import useUser from "../../hooks/useUser";
import useNotifications from "../../hooks/useNotifications";
import NotificationModal from "./NotificationModal";
import axiosInstance from "../../utils/axiosConfig";
import { getMobileEnvironmentInfo } from "../../utils/mobileDetection";
import {
  buildTimeInClientContext,
  buildTimeOutRequestBody,
  getOrCreateDeviceId,
} from "../../utils/trustedDevice";
import TrustedDeviceRegistrationModal from "../../features/trusted-devices/components/TrustedDeviceRegistrationModal";
import {
  handleAttendanceClockError,
  isDeviceNotTrustedError,
  postTimeIn,
  postTimeOut,
  readApiErrorPayload,
} from "../../features/trusted-devices/api/trustedDevicesApi";
import usePendingAttendanceCount from "../../hooks/usePendingAttendanceCount";

interface ConfirmModalProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}
const ConfirmModal: React.FC<ConfirmModalProps> = ({
  message,
  onConfirm,
  onCancel,
}) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded shadow-lg p-6 max-w-sm w-full">
        <p className="text-gray-800">{message}</p>
        <div className="flex justify-end mt-4">
          <button
            onClick={onCancel}
            className="px-4 py-2 mr-2 bg-gray-300 rounded hover:bg-gray-400 transition duration-300"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition duration-300"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// Utility function to check if cookies are enabled
function areCookiesEnabled() {
  try {
    document.cookie = "__cookie_test=1; SameSite=None; Secure";
    const cookiesEnabled = document.cookie.indexOf("__cookie_test=1") !== -1;
    // Clean up
    document.cookie =
      "__cookie_test=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure";
    return cookiesEnabled;
  } catch (e) {
    return false;
  }
}

// Modal to show cookie warning
const CookieWarningModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
    <div className="bg-white rounded shadow-lg p-6 max-w-sm w-full">
      <h2 className="text-lg font-bold mb-2 text-red-600">Cookies Required</h2>
      <p className="text-gray-800 mb-4">
        This site requires 3rd party cookies to be enabled for login and session
        management. Please enable 3rd party cookies in your browser settings and
        refresh the page.
      </p>
      <button
        onClick={onClose}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition duration-300"
      >
        Close
      </button>
    </div>
  </div>
);

const Header: React.FC = () => {
  const [isTimedIn, setIsTimedIn] = useState(false);
  const [timer, setTimer] = useState(0);
  const [timeStatusChecked, setTimeStatusChecked] = useState(false);
  const [isTimeToggleLoading, setIsTimeToggleLoading] = useState(false);
  const [showTimeoutConfirm, setShowTimeoutConfirm] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const { user, loading } = useUser();
  const { notifications, unreadCount, markAsRead, fetchNotifications } =
    useNotifications(user?._id);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCookieWarning, setShowCookieWarning] = useState(false);
  const [showTrustedDeviceModal, setShowTrustedDeviceModal] = useState(false);
  const [trustedDeviceModalId, setTrustedDeviceModalId] = useState<
    string | undefined
  >();

  // Pending attendance approval count — manager / SuperAdmin only
  const isApprover = user?.role === "manager" || user?.role === "SuperAdmin";
  const pendingApprovalCount = usePendingAttendanceCount(
    !loading && isApprover,
  );

  /**
   * Formats seconds into HH:MM:SS format.
   * @param seconds Number of seconds.
   * @returns Formatted time string.
   */
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0",
    )}:${String(secs).padStart(2, "0")}`;
  };

  /**
   * Starts the timer with an initial count.
   * @param initialSeconds Initial seconds to start the timer.
   */
  const startTimer = (initialSeconds = 0) => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimer(initialSeconds);
    timerRef.current = setInterval(() => {
      setTimer((prevTimer) => prevTimer + 1);
    }, 1000);
  };

  /**
   * Checks the current active attendance status of the user.
   */
  const checkActiveAttendance = async () => {
    if (!user?._id) return;

    try {
      const response = await axiosInstance.get(
        `${backendUrl}/api/attendance/user/${user._id}/active`,
        { withCredentials: true },
      );

      const { hasActiveAttendance, attendance } = response.data;

      if (
        hasActiveAttendance &&
        attendance &&
        attendance.type !== "Absent" &&
        attendance.timeIn &&
        !isNaN(new Date(attendance.timeIn).getTime())
      ) {
        setIsTimedIn(true);
        const elapsedTime = Math.floor(
          (Date.now() - new Date(attendance.timeIn).getTime()) / 1000,
        );
        startTimer(elapsedTime);
      } else {
        setIsTimedIn(false);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setTimer(0);
      }
    } catch (error: any) {
      console.error("Error fetching active attendance:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to retrieve active attendance status";
      toast.error(errorMessage);

      setIsTimedIn(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimer(0);
    } finally {
      setTimeStatusChecked(true);
    }
  };

  useEffect(() => {
    if (!loading && user) {
      checkActiveAttendance();
    } else if (!loading && !user) {
      // If user is not fetched (null) after loading,
      // mark time status as checked so we don't wait indefinitely.
      setTimeStatusChecked(true);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  useEffect(() => {
    // Check for 3rd party cookie support on mount
    if (!areCookiesEnabled()) {
      setShowCookieWarning(true);
    }
  }, []);

  /**
   * Handles toggling between Time In and Time Out.
   */
  const handleTimeToggle = async () => {
    if (isTimeToggleLoading) return; // Prevent duplicate clicks

    // If the user is currently timed in, ask for confirmation before timing out.
    if (isTimedIn) {
      setShowTimeoutConfirm(true);
      return;
    }

    // Time In action
    setIsTimeToggleLoading(true);
    if (!user?._id) {
      toast.error("User information not available");
      setIsTimeToggleLoading(false);
      return;
    }

    const mobileEnvironment = getMobileEnvironmentInfo();
    if (mobileEnvironment.isMobile && !user.areCool) {
      toast.error(
        "Time in is not allowed from mobile devices. Please use your office laptop.",
      );
      setIsTimeToggleLoading(false);
      return;
    }

    try {
      const clientContext = buildTimeInClientContext(mobileEnvironment, user);

      const requestBody = {
        userId: user._id,
        ...clientContext,
      };

      const response = await postTimeIn(requestBody);
      if (response.status === 201) {
        setIsTimedIn(true);
        startTimer();
        toast.success("Successfully Timed In.");
      }
    } catch (error: unknown) {
      console.error("Error logging time:", error);
      if (isDeviceNotTrustedError(error)) {
        const { deviceId, message } = readApiErrorPayload(error);
        setTrustedDeviceModalId(deviceId || getOrCreateDeviceId());
        setShowTrustedDeviceModal(true);
        toast.info(
          message ||
            "This device must be registered and approved by HR before clock-in.",
        );
      } else {
        toast.error(readApiErrorPayload(error).message);
      }
    } finally {
      setIsTimeToggleLoading(false);
    }
  };

  /**
   * Handles confirming the Time Out action.
   */
  const handleConfirmTimeout = async () => {
    setIsTimeToggleLoading(true);
    setShowTimeoutConfirm(false);

    if (!user?._id) {
      toast.error("User information not available");
      setIsTimeToggleLoading(false);
      return;
    }

    const mobileEnvironment = getMobileEnvironmentInfo();
    if (mobileEnvironment.isMobile && !user.areCool) {
      toast.error(
        "Time out is not allowed from mobile devices. Please use your office laptop.",
      );
      setIsTimeToggleLoading(false);
      return;
    }

    try {
      const requestBody = buildTimeOutRequestBody(
        user._id,
        mobileEnvironment,
        user,
      );

      const response = await postTimeOut(requestBody);
      if (response.status === 200) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setIsTimedIn(false);
        toast.warning("Successfully Timed Out.");
      }
    } catch (error: unknown) {
      console.error("Error logging time:", error);
      handleAttendanceClockError(error, {
        onTrustedDeviceRequired: (deviceId, message) => {
          setTrustedDeviceModalId(deviceId);
          setShowTrustedDeviceModal(true);
          toast.info(message);
        },
        showError: (message) => toast.error(message),
      });
    } finally {
      setIsTimeToggleLoading(false);
    }
  };

  /**
   * Handles cancelling the Time Out confirmation.
   */
  const handleCancelTimeout = () => {
    setShowTimeoutConfirm(false);
  };

  /**
   * Handles logging out the user.
   */
  const handleLogout = async () => {
    try {
      await axiosInstance.post(
        `${backendUrl}/api/users/logout`,
        {},
        { withCredentials: true },
      );
      navigate("/login");
      toast.success("Logged out successfully.");
    } catch (error: any) {
      console.error("Logout failed:", error);
      const errorMessage =
        error.response?.data?.message || "Logout failed. Please try again.";
      toast.error(errorMessage);
    }
  };

  /**
   * Opens the notification modal.
   */
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  /**
   * Closes the notification modal and fetches updated notifications.
   */
  const handleCloseModal = async () => {
    await fetchNotifications();
    setIsModalOpen(false);
  };

  return (
    <header className="mt-1">
      {/* Cookie Warning Modal */}
      {showCookieWarning && (
        <CookieWarningModal onClose={() => setShowCookieWarning(false)} />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Company Logo */}
          <div className="flex items-center ml-2 sm:ml-2">
            <img
              src={MarTechLogo}
              alt="Company logo"
              className="h-6 w-auto sm:h-5 md:h-10"
            />
          </div>

          <div className="flex flex-wrap gap-1 justify-end items-center md:flex-nowrap md:space-x-3">
            {/* Notification Button */}
            {!loading && user && (
              <>
                <button
                  onClick={handleOpenModal}
                  className="relative p-2 rounded-full bg-purple-900 text-white hover:bg-purple-800 transition duration-300"
                  aria-label="Notifications"
                >
                  <IoNotificationsSharp size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full px-1">
                      {unreadCount}
                    </span>
                  )}
                </button>
                <NotificationModal
                  isOpen={isModalOpen}
                  onClose={handleCloseModal}
                  notifications={notifications}
                  markAsRead={async (notificationId: string) => {
                    await markAsRead(notificationId);
                    await fetchNotifications();
                  }}
                />
              </>
            )}

            {/* Pending attendance approvals badge — manager / SuperAdmin */}
            {!loading && isApprover && (
              <Link
                to="/organization/attendance-approval"
                className="relative p-2 rounded-full bg-orange-600 text-white hover:bg-orange-500 transition duration-300"
                aria-label="Attendance Approval Requests"
                title="Attendance Approval Requests"
              >
                <FaClipboardList size={20} />
                {pendingApprovalCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {pendingApprovalCount}
                  </span>
                )}
              </Link>
            )}

            {/* Render timer and time buttons only after time status is checked */}
            {!loading && user && timeStatusChecked && (
              <>
                {/* Timer Display when Timed In */}
                {isTimedIn && (
                  <div className="flex items-center">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        maxWidth: "200px",
                        height: "7px",
                        backgroundColor: "#22C55E",
                        borderRadius: "9999px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: `${((timer % 3600) / 3600) * 100}%`,
                          background: "#22C55E",
                          transition: "width 0.5s ease-in-out",
                        }}
                      ></div>
                    </div>

                    <div
                      style={{
                        background: "#22C55E",
                        color: "white",
                        borderRadius: "9999px",
                        padding: "5px 16px",
                        fontFamily: "'Roboto Mono', monospace",
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        animation: "pulse 1.5s infinite",
                      }}
                    >
                      {formatTime(timer)}
                    </div>
                  </div>
                )}

                {/* Time In/Out Button */}
                <button
                  onClick={handleTimeToggle}
                  disabled={isTimeToggleLoading}
                  className={`flex items-center w-25 gap-2 px-4 py-2 rounded-full text-white uppercase font-medium text-xs sm:text-xs md:text-base ${
                    isTimedIn
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-green-600 hover:bg-green-700"
                  }`}
                >
                  {isTimedIn ? (
                    <>
                      <MdOutlineTimerOff size={18} />
                      <span>Time Out</span>
                    </>
                  ) : (
                    <>
                      <MdOutlineMoreTime size={18} />
                      <span>Time In</span>
                    </>
                  )}
                </button>
              </>
            )}

            {/* Logout Button */}
            {!loading && user && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-black text-white uppercase font-medium text-xs sm:text-xs md:text-base hover:bg-neutral-800 transition duration-300"
              >
                <RiLogoutCircleRLine size={18} />
                <span>Log Out</span>
              </button>
            )}

            {loading && <div className="text-gray-500"></div>}
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Time Out */}
      {showTimeoutConfirm && (
        <ConfirmModal
          message="Are you sure you want to Time Out?"
          onConfirm={handleConfirmTimeout}
          onCancel={handleCancelTimeout}
        />
      )}

      <TrustedDeviceRegistrationModal
        isOpen={showTrustedDeviceModal}
        deviceId={trustedDeviceModalId}
        onClose={() => setShowTrustedDeviceModal(false)}
      />
    </header>
  );
};

export default Header;
